import { Link } from "react-router-dom";
import {
  Envelope, MapPin, ShieldCheck, ChartLineUp, Buildings, ArrowRight,
  CreditCard, Handshake, Scales, ClockClockwise, LinkedinLogo,
} from "@phosphor-icons/react";
import Logo from "@/components/Logo";
import { SiteFooter } from "@/pages/Landing";

/**
 * /contact — Public business contact page.
 *
 * Required by Paddle Merchant of Record diligence:
 *   - Clearly identifies the publisher (Ibn Miyeta Group).
 *   - Provides dedicated email addresses for support, billing, privacy and legal.
 *   - Links to legal policies (Terms, Privacy, Refund).
 *   - Lists product SLAs and business hours.
 *
 * Kept intentionally text-heavy and text-searchable. No JS-only interactions.
 */
export default function Contact() {
  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="contact-page">
      <header className="border-b border-border bg-card sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Logo variant="wordmark" size={32} to="/" testId="contact-logo" />
          <div className="flex items-center gap-3">
            <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground font-medium hidden sm:inline" data-testid="contact-signin">Sign in</Link>
            <Link to="/pricing" className="text-sm bg-primary text-primary-foreground rounded-lg px-4 py-2 font-semibold hover:bg-primary/90 transition-colors" data-testid="contact-pricing">See plans</Link>
          </div>
        </div>
      </header>

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
        <div className="text-overline text-primary mb-3">Contact</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] max-w-3xl" data-testid="contact-title">
          We&apos;re here to help.
        </h1>
        <p className="mt-5 text-sm sm:text-base text-muted-foreground max-w-2xl leading-relaxed" data-testid="contact-subtitle">
          Orixa Invest is a subscription SaaS platform developed by Ibn Miyeta Group. For any question about
          your software subscription, billing, privacy or a legal notice, use the appropriate channel below.
        </p>

        {/* Contact channels */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5" data-testid="contact-channels">
          <ChannelCard
            testid="contact-support"
            icon={ChartLineUp}
            label="Product & customer support"
            email="support@orixa-invest.co"
            sla="Business hours (Mon–Fri, 9:00–18:00 UTC) · Response within 1 business day for paid plans."
          />
          <ChannelCard
            testid="contact-billing"
            icon={CreditCard}
            label="Billing, subscriptions & refunds"
            email="billing@orixa-invest.co"
            sla="Response within 1 business day. Payments are processed by Paddle (Merchant of Record)."
          />
          <ChannelCard
            testid="contact-privacy"
            icon={ShieldCheck}
            label="Privacy, GDPR & data-subject requests"
            email="privacy@orixa-invest.co"
            sla="Response within 30 days as required by GDPR."
          />
          <ChannelCard
            testid="contact-legal"
            icon={Scales}
            label="Legal notices"
            email="legal@orixa-invest.co"
            sla="Formal legal notices only. Do not use for support questions."
          />
        </div>

        {/* Company block */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-5">
          <div className="bg-card border border-border rounded-2xl p-6" data-testid="contact-company">
            <div className="flex items-center gap-2 text-overline text-muted-foreground mb-3">
              <Buildings size={12} weight="bold" /> Publisher
            </div>
            <div className="font-display text-lg tracking-tight">Ibn Miyeta Group</div>
            <div className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Orixa Invest is a subscription-based software platform developed by Ibn Miyeta Group.
              Payments are processed by Paddle acting as Merchant of Record.
            </div>
            <a
              href="https://www.ibnmiyetagroup.org"
              target="_blank" rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline"
              data-testid="contact-group-link"
            >
              <LinkedinLogo size={14} weight="bold" /> Visit Ibn Miyeta Group
            </a>
          </div>

          <div className="bg-card border border-border rounded-2xl p-6" data-testid="contact-address">
            <div className="flex items-center gap-2 text-overline text-muted-foreground mb-3">
              <MapPin size={12} weight="bold" /> Registered contact
            </div>
            <div className="text-sm text-muted-foreground leading-relaxed">
              For formal legal correspondence, please use{" "}
              <a href="mailto:legal@orixa-invest.co" className="text-primary underline">legal@orixa-invest.co</a>.
              A postal address can be provided on request for regulatory or procurement purposes.
            </div>
          </div>

          <div className="bg-card border border-border rounded-2xl p-6" data-testid="contact-hours">
            <div className="flex items-center gap-2 text-overline text-muted-foreground mb-3">
              <ClockClockwise size={12} weight="bold" /> Business hours & SLA
            </div>
            <ul className="text-sm text-muted-foreground space-y-1.5">
              <li>Support: Mon–Fri · 09:00–18:00 UTC</li>
              <li>Billing: 1 business day response</li>
              <li>Privacy / GDPR: up to 30 days</li>
              <li>Emergency incident: paid customers have a dedicated escalation email in-app.</li>
            </ul>
          </div>
        </div>

        {/* Alternative channels */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-5">
          <div className="bg-secondary/40 border border-border rounded-2xl p-6" data-testid="contact-demo">
            <div className="text-overline text-primary mb-3">Book a product demo</div>
            <div className="font-display text-xl tracking-tight">See the platform in action</div>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              A 30-minute walkthrough of the software — country intelligence, AI research pipeline, downloadable
              institutional reports and collaboration workspaces.
            </p>
            <a
              href="https://cal.com/romuald-ouattara-wttutm/call-orixa-invest"
              target="_blank" rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-4 py-2.5 text-sm font-semibold hover:opacity-90 card-elev"
              data-testid="contact-book-cta"
            >
              Book a product demo <ArrowRight size={14} weight="bold" />
            </a>
          </div>

          <div className="bg-secondary/40 border border-border rounded-2xl p-6" data-testid="contact-enterprise">
            <div className="text-overline text-primary mb-3">Enterprise & procurement</div>
            <div className="font-display text-xl tracking-tight">Custom MSAs, DPAs & invoicing</div>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              For large teams and cross-border mandates. Volume discounts, security questionnaires, custom
              contracts and consolidated invoicing.
            </p>
            <a
              href="mailto:sales@orixa-invest.co?subject=Enterprise%20plan%20-%20Orixa%20Invest"
              className="mt-4 inline-flex items-center gap-2 border border-border bg-card rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-secondary"
              data-testid="contact-enterprise-cta"
            >
              <Handshake size={14} weight="bold" /> Contact enterprise sales
            </a>
          </div>
        </div>

        {/* Legal quick links */}
        <div className="mt-12 border-t border-border pt-8">
          <div className="text-overline text-muted-foreground mb-3">Legal &amp; policies</div>
          <div className="flex flex-wrap gap-3 text-sm">
            <Link to="/legal/terms"   className="text-primary underline underline-offset-4 hover:text-foreground" data-testid="contact-terms">Terms of Service</Link>
            <Link to="/legal/privacy" className="text-primary underline underline-offset-4 hover:text-foreground" data-testid="contact-privacy-link">Privacy Policy</Link>
            <Link to="/legal/refund"  className="text-primary underline underline-offset-4 hover:text-foreground" data-testid="contact-refund">Refund Policy</Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}

function ChannelCard({ icon: Icon, label, email, sla, testid }) {
  return (
    <div className="bg-card border border-border rounded-2xl p-6 hover:card-elev transition-shadow" data-testid={testid}>
      <div className="flex items-center gap-3">
        <div className="h-10 w-10 rounded-lg bg-primary/10 text-primary grid place-items-center flex-shrink-0">
          <Icon size={18} weight="duotone" />
        </div>
        <div className="text-overline text-muted-foreground">{label}</div>
      </div>
      <a
        href={`mailto:${email}`}
        className="mt-4 inline-flex items-center gap-2 font-display text-lg sm:text-xl tracking-tight text-primary hover:underline break-all"
        data-testid={`${testid}-email`}
      >
        <Envelope size={16} weight="bold" /> {email}
      </a>
      <p className="mt-3 text-xs text-muted-foreground leading-relaxed">{sla}</p>
    </div>
  );
}
